import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { HeaderMeta } from '@/components/layout/header-meta';
import { LogIn, Search } from 'lucide-react';
import { getScaledUrl } from '@/lib/cloudinary';
import {
  useLandingSearch,
  type LandingEventFilter,
} from '@/components/landing/landing-search-context';
import { LiveNowHero } from '@/components/landing/LiveNowHero';

/** Live scorebug and weather-delay mockup. */
const PRODUCT_MOCKUP =
  'https://res.cloudinary.com/dnukl7bmh/image/upload/v1770735551/chat-edited-1770734941345-x5vl82be2xr_uaka5b.png';

/** Nav logo. Same Cloudinary asset as the favicon, sized for the header. */
const LOGO_URL =
  'https://res.cloudinary.com/dnukl7bmh/image/upload/f_auto,q_auto,w_360/v1770494186/Transparent_File_-01_tmvfob.png';

const EVENT_PILLS: { id: LandingEventFilter; label: string }[] = [
  { id: 'active', label: 'Active events' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'all', label: 'All events' },
];

function scrollToDirectory() {
  document.getElementById('tournaments-directory')?.scrollIntoView({ behavior: 'smooth' });
}

export function HeroBroadcast() {
  const containerRef = useRef<HTMLDivElement>(null);
  const landingSearch = useLandingSearch();
  const { scrollY } = useScroll();
  const navShadow = useTransform(
    scrollY,
    [0, 80],
    ['0 0 0 transparent', '0 4px 30px rgba(0,0,0,0.3)']
  );

  const searchQuery = landingSearch?.searchQuery ?? '';
  const eventFilter = landingSearch?.eventFilter ?? 'all';

  return (
    <section
      ref={containerRef}
      className="relative overflow-hidden bg-slate-950 text-white"
      data-testid="hero-broadcast"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 80% at 50% 0%, #0f172a 0%, #020617 55%, #020617 100%)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-48 opacity-40"
        style={{
          background:
            'radial-gradient(ellipse 70% 100% at 50% 100%, rgba(16,185,129,0.35) 0%, transparent 70%)',
        }}
        aria-hidden
      />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(212,175,55,0.18) 0%, transparent 60%)',
        }}
      />

      <motion.header
        style={{ boxShadow: navShadow }}
        className="sticky top-0 z-50 bg-sport-black/80 backdrop-blur-xl border-b border-white/10"
      >
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link href="/" className="flex-shrink-0" aria-label="Dugout Desk Home">
              <img
                src={LOGO_URL}
                alt="Dugout Desk"
                className="h-12 w-12 object-contain aspect-square md:h-[7.5rem] md:w-[7.5rem]"
                loading="eager"
                decoding="async"
                width={360}
                height={360}
              />
            </Link>
            <div className="flex items-center gap-2 md:gap-3">
              <HeaderMeta variant="dark" />
              <Button
                asChild
                variant="outline"
                size="sm"
                className="bg-white/10 text-white border-white/30 hover:bg-white/20 font-broadcast min-h-[48px] min-w-[48px] touch-manipulation"
              >
                <Link href="/login" aria-label="Login">
                  <LogIn className="w-4 h-4 mr-0 sm:mr-2" />
                  <span className="hidden sm:inline">Login</span>
                </Link>
              </Button>
              <Button
                asChild
                size="sm"
                className="bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-broadcast uppercase tracking-wider min-h-[48px] touch-manipulation"
              >
                <Link href="/register">
                  <span className="sm:hidden">Create</span>
                  <span className="hidden sm:inline">Create Your Organization</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </motion.header>

      <LiveNowHero />

      <div className="relative container mx-auto px-4 pt-12 md:pt-20 pb-16 md:pb-24">
        <div className="max-w-5xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <img
              src={
                getScaledUrl(PRODUCT_MOCKUP, 1200) ||
                'https://res.cloudinary.com/dnukl7bmh/image/upload/f_auto,q_auto,dpr_auto,w_1200,c_limit/v1770735551/chat-edited-1770734941345-x5vl82be2xr_uaka5b.png'
              }
              alt="Don't just schedule it. Broadcast it."
              className="mx-auto h-auto w-full max-w-4xl object-contain"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              width={1200}
              height={675}
            />
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mt-6 md:mt-8 text-lg md:text-xl text-white/70 max-w-2xl mx-auto font-sans"
          >
            The tournament operations engine that turns weekend chaos into a prime-time event.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="mt-8 md:mt-10 flex justify-center"
          >
            <Button
              asChild
              size="lg"
              className="bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-broadcast uppercase tracking-wider text-base md:text-lg px-8 py-6 shadow-[0_0_30px_rgba(16,185,129,0.35)] min-h-[48px] touch-manipulation"
            >
              <Link href="/register">Create Your Organization</Link>
            </Button>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.45 }}
            className="mt-8 md:mt-10 max-w-2xl mx-auto text-left"
            onSubmit={event => {
              event.preventDefault();
              scrollToDirectory();
            }}
          >
            <label htmlFor="landing-tournament-search" className="sr-only">
              Find your tournament or organization
            </label>
            <div className="relative rounded-xl bg-white text-slate-950 shadow-2xl">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500"
                aria-hidden
              />
              <Input
                id="landing-tournament-search"
                type="search"
                value={searchQuery}
                onChange={event => landingSearch?.setSearchQuery(event.target.value)}
                placeholder="Find your tournament or organization..."
                className="h-14 min-h-[56px] border-0 bg-white pl-12 pr-4 text-base text-slate-950 placeholder:text-slate-500 focus-visible:ring-emerald-500 focus-visible:ring-offset-0"
                data-testid="input-find-tournament"
              />
            </div>
            <div
              className="mt-3 flex flex-wrap justify-center gap-2"
              role="group"
              aria-label="Event filters"
            >
              {EVENT_PILLS.map(pill => {
                const selected = eventFilter === pill.id;
                return (
                  <button
                    key={pill.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => {
                      landingSearch?.setEventFilter(pill.id);
                      scrollToDirectory();
                    }}
                    className={`min-h-11 rounded-full px-4 text-sm font-broadcast uppercase tracking-wide touch-manipulation ${
                      selected
                        ? 'bg-emerald-500 text-slate-950'
                        : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                    }`}
                  >
                    {pill.label}
                  </button>
                );
              })}
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
